/**
 * What field staff may open on the web.
 *
 * PCAs do their work in the AraSync phone app: their clients, schedule, visits
 * and visit notes live there. On the web they were landing on the program
 * console and the Home Health program workspace, which show every client's
 * authorizations, every caregiver's visits and the intake pipeline
 * (reported 2026-10-07). Their role keeps the permissions the phone needs, so
 * the web narrows them here: their own time, messages and profile and the
 * training videos made for them, nothing about other clients or other staff. The server enforces the same rule on
 * the data (middlewares/fieldStaffGuard.js).
 */

// Direct-care staff who see only their own clients and their own work.
export const FIELD_STAFF_PROVIDER_TYPES = Object.freeze(['PCA', 'Caregiver', 'DSP', 'Driver']);

const ADMIN_ROLES = ['admin', 'super_admin'];

// Pages field staff may open. '/admin' is their home (FieldStaffWebHome).
// The Training Library (2026-10-07) shows only the videos whose audience
// includes them; the server filters the list and every play link.
export const FIELD_STAFF_WEB_PATHS = Object.freeze([
  '/admin/time-tracking',
  '/admin/communication',
  '/admin/profile',
  '/admin/training',
]);

/** True for a signed-in field-staff member who is not also an admin. */
export function isFieldStaffWebUser(user) {
  if (!user || ADMIN_ROLES.includes(user.role)) return false;
  return FIELD_STAFF_PROVIDER_TYPES.includes(user.providerType);
}

/**
 * Whether field staff may open this page. Anything else, the dashboard at
 * '/admin' included, renders their home (FieldStaffWebHome) instead; the shell
 * first moves them to '/admin' (fieldStaffHomeRedirect).
 */
export function fieldStaffMayOpen(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  return FIELD_STAFF_WEB_PATHS.some((allowed) => path === allowed || path.startsWith(`${allowed}/`));
}

/**
 * Where field staff land on a page they may not open: their home at '/admin'
 * itself, so the address and the breadcrumb say home too (QA M2, 2026-10-20:
 * a 245D program URL showed their home under "Programs > 245D Basic"). Null
 * for '/admin' and their own pages.
 */
export function fieldStaffHomeRedirect(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  if (path === '/admin' || fieldStaffMayOpen(path)) return null;
  return '/admin';
}

const keepsHref = (href) => href === '/admin' || fieldStaffMayOpen(href);

/**
 * The sidebar for field staff: Dashboard (their home) and the allowed pages;
 * a section keeps only its allowed children and disappears without any. A
 * section left with one page becomes a direct link to it (Communication ›
 * Messaging shows as Messaging, per the approved spec). Never mutates the
 * full navigation.
 */
export function trimNavigationForFieldStaff(items) {
  return (Array.isArray(items) ? items : []).flatMap((item) => {
    if (Array.isArray(item.children)) {
      const children = item.children.filter((child) => keepsHref(child.href));
      if (children.length === 1) {
        const [only] = children;
        return [{ ...only, id: only.id || item.id, icon: only.icon || item.icon }];
      }
      return children.length ? [{ ...item, children }] : [];
    }
    return keepsHref(item.href) ? [item] : [];
  });
}
